import { ResponseLatency } from '../../../../store/modules/merchant/types';

/** Format a 0–1 rate as a whole-number percentage string (e.g. 0.94 → "94%"). */
export const formatRate = (rate: number | undefined | null): string => {
  if (rate === undefined || rate === null || Number.isNaN(rate)) {
    return '—';
  }
  return `${Math.round(rate * 100)}%`;
};

export const percentShares = (counts: number[]): number[] => {
  const total = counts.reduce((sum, count) => sum + count, 0);
  const exact = counts.map((count) => (total === 0 ? 0 : (count / total) * 100));
  const percentages = exact.map(Math.floor);
  let remaining = total === 0 ? 0 : 100 - percentages.reduce((sum, value) => sum + value, 0);
  const order = exact
    .map((value, index) => ({ index, remainder: value - percentages[index] }))
    .sort((a, b) => b.remainder - a.remainder || a.index - b.index);
  for (const entry of order) {
    if (remaining <= 0) {
      break;
    }
    percentages[entry.index] += 1;
    remaining -= 1;
  }
  return percentages;
};

/** Format seconds as "Xm XXs" (e.g. 184.5 → "3m 04s"). */
export const formatDuration = (seconds: number | undefined | null): string => {
  if (seconds === undefined || seconds === null || Number.isNaN(seconds)) {
    return '—';
  }
  const total = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(total / 60);
  const secs = total % 60;
  return `${minutes}m ${secs.toString().padStart(2, '0')}s`;
};

/** Format latency milliseconds for display (e.g. 842 → "842ms", 1500 → "1.5s"). */
export const formatLatency = (ms: number | undefined | null): string => {
  if (ms === undefined || ms === null || Number.isNaN(ms)) {
    return '—';
  }
  if (ms >= 1000) {
    return `${(ms / 1000).toFixed(1)}s`;
  }
  return `${Math.round(ms)}ms`;
};

export const formatCount = (count: number | undefined | null): string => {
  if (count === undefined || count === null || Number.isNaN(count)) {
    return '—';
  }
  return count.toLocaleString();
};

type LatencyCardCopy = {
  value: string;
  description: string;
};

const LATENCY_DESCRIPTION = 'Time to first reply';

/** Card value for the median shopper wait; the subtag stays fixed. */
export const describeResponseLatency = (
  latency: ResponseLatency | undefined | null,
): LatencyCardCopy => {
  const p50Ms = latency?.p50Ms ?? null;
  const measured = latency?.measuredResponses ?? 0;

  if (p50Ms === null || measured === 0) {
    return { value: '—', description: LATENCY_DESCRIPTION };
  }
  return { value: formatLatency(p50Ms), description: LATENCY_DESCRIPTION };
};

/** Format a 0–1 drift score as a percentage with one decimal, or "—" when null. */
export const formatDriftScore = (score: number | null | undefined): string => {
  if (score === undefined || score === null || Number.isNaN(score)) {
    return '—';
  }
  return `${(score * 100).toFixed(1)}%`;
};

export type DriftLevel = 'unknown' | 'stable' | 'watch' | 'high';

/** Upper bounds (exclusive) for each drift band, per MMS scoring guidance. */
export const DRIFT_STABLE_MAX = 0.1;
export const DRIFT_WATCH_MAX = 0.25;

export const getDriftLevel = (score: number | null | undefined): DriftLevel => {
  if (score === undefined || score === null || Number.isNaN(score)) {
    return 'unknown';
  }
  if (score < DRIFT_STABLE_MAX) {
    return 'stable';
  }
  if (score <= DRIFT_WATCH_MAX) {
    return 'watch';
  }
  return 'high';
};

export const DRIFT_LEVEL_LABEL: Record<DriftLevel, string> = {
  unknown: 'No data',
  stable: 'Stable',
  watch: 'Watch',
  high: 'High drift',
};
